<template>
  <div class="container">
    <h1>Panel de Administración de Reservaciones</h1>

    <!-- Alerta de Nueva Solicitud -->
    <button v-if="newReservationAlert" @click="goToNewReservation" class="blinking-button">
      ¡Nueva solicitud de reservación!
    </button>

    <button @click="openReservationModal">Añadir Nueva Reservación</button>

    <div v-if="showReservationModal" class="modal">
      <div class="modal-content">
        <AdminReservationView @reservation-complete="closeReservationModal" />
        <button @click="closeReservationModal">Cerrar</button>
      </div>
    </div>

    <!-- Escáner de QR -->
    <button @click="startQRCodeScanner">Escanear QR Code</button>
    <video ref="videoRef" v-if="scanning"></video>

    <!-- Botón para abrir las reservaciones escaneadas -->
    <button @click="openScannedReservationsModal">Ver Reservaciones Escaneadas</button>

    <!-- Modal de reservaciones escaneadas -->
    <div v-if="showScannedReservationsModal" class="modal">
      <div class="modal-content">
        <ReservationHistoryView @close="closeScannedReservationsModal" />
        <button @click="closeScannedReservationsModal" class="close-modal-btn">Cerrar</button>
      </div>
    </div>

    <!-- Modal para mostrar la vista QRCodeView -->
    <div v-if="showQRCodeModal" class="modal">
      <div class="modal-content">
        <QRCodeView :reservation="selectedReservation" @close="closeQRCodeModal" />
        <button @click="closeQRCodeModal" class="close-modal-btn">Cerrar</button>
      </div>
    </div>

     <div>
    <button @click="openScheduleModal">Gestionar Horarios</button>

    <!-- Modal para editar horarios -->
    <div v-if="showScheduleModal" class="modal">
      <div class="modal-content">
        <h3>Gestión de Horarios</h3>
        <input type="date" v-model="selectedDate" @change="loadTimesForDate" />

        <div v-if="selectedDate">
          <h4>Horarios para {{ selectedDate }}</h4>
          <ul>
            <li v-for="time in allPossibleTimes" :key="time">
              <span>{{ formatTimeTo12Hour(time) }}</span>
              <button @click="toggleTime(selectedDate, time)">
                {{ isTimeDisabled(selectedDate, time) ? 'Activar' : 'Desactivar' }}
              </button>
            </li>
          </ul>
        </div>

        <button @click="closeScheduleModal">Cerrar</button> <!-- Botón para cerrar -->
      </div>
    </div>
  </div>
      
    <!-- Filtro de búsqueda por email o teléfono -->
    <div>
      <input 
        v-model="searchQuery" 
        placeholder="Buscar por email o teléfono" 
        @input="filterReservations" 
        class="search-bar" 
      />
    </div>

    <!-- Sección de Reservaciones No Atendidas -->
    <div v-if="pendingReservations.length" class="pending-reservations">
      <h2>Solicitudes No Atendidas</h2>
      <ul>
        <li v-for="reservation in pendingReservations" :key="reservation.id">
          {{ reservation.name }} - {{ reservation.date }} {{ formatTime(reservation.time) }}
          <button @click="selectReservationForAction(reservation.id)">Ver Detalles</button>
        </li>
      </ul>
    </div>

    <!-- Sección de Reservaciones Atendidas -->
    <div class="reservation-cards">
      <div
        v-for="reservation in filteredReservations"
        :key="reservation.id"
        :id="'reservation-' + reservation.id"
        :class="['reservation-card', getStatusClass(reservation.status)]"
      >
        <h3>{{ reservation.name }}</h3>
        <p><strong>Fecha:</strong> {{ reservation.date }}</p>
        <p><strong>Hora:</strong> {{ formatTime(reservation.time) }}</p>
        <p><strong>Personas:</strong> {{ reservation.people }}</p>
        <p><strong>Estado:</strong> {{ reservation.status }}</p>
        <p v-if="reservation.tableNumber"><strong>Mesa:</strong> {{ reservation.tableNumber }}</p>
        <p v-if="reservation.phone"><strong>Teléfono:</strong> {{ reservation.phone }}</p>
        <p v-if="reservation.specialNotes"><strong>Notas Especiales:</strong> {{ reservation.specialNotes }}</p>

        <div v-if="reservation.status !== 'cancelled'">
          <label for="tableNumberInput">Asignar Mesa:</label>
          <input v-model="reservation.tableNumberInput" id="tableNumberInput" placeholder="Número de Mesa" :disabled="reservation.tableAssigned" />
          <button
            @click="assignTable(reservation)"
            :disabled="loading || reservation.tableAssigned"
            :class="{'table-assigned': reservation.tableAssigned, 'button-disabled': reservation.tableAssigned}"
          >
            <template v-if="loading">Asignando...</template>
            <template v-else>Asignar Mesa</template>
          </button>

          <button @click="confirmReservation(reservation)" :disabled="loading || reservation.status === 'confirmed'">
           <template v-if="reservation.status === 'confirmed'">Confirmada</template>
           <template v-else>Confirmar</template>
          </button>


          <button @click="denyReservation(reservation)" :disabled="loading">
            <template v-if="loading">Denegando...</template>
            <template v-else>Denegar Reservación</template>
          </button>

          <button @click="cancelReservation(reservation)" :disabled="loading">
            <template v-if="loading">Cancelando...</template>
            <template v-else>Cancelar Reservación</template>
          </button>

          <!-- Botón para abrir el modal de mensajes -->
          <button @click="openMessageModal(reservation)">Contactar por SMS</button>

          <button @click="openEditModal(reservation)">Editar</button>
          <button @click="deleteReservation(reservation)" :disabled="loading">Eliminar</button>
        </div>
      </div>
    </div>

    <!-- Modal de Edición -->
    <div v-if="showEditModal" class="modal">
      <div class="modal-content">
        <h2>Editar Reservación</h2>
        <form @submit.prevent="updateReservation(selectedReservation)">
          <div>
            <label for="editName">Nombre:</label>
            <input type="text" v-model="selectedReservation.name" id="editName" required>
          </div>
          <div>
            <label for="editEmail">Email:</label>
            <input type="email" v-model="selectedReservation.email" id="editEmail" required>
          </div>
          <div>
            <label for="editPhone">Teléfono:</label>
            <input type="tel" v-model="selectedReservation.phone" id="editPhone" required>
          </div>
          <div>
            <label for="editDate">Fecha:</label>
            <input type="date" v-model="selectedReservation.date" id="editDate" required>
          </div>
          <div>
            <label for="editTime">Hora:</label>
            <select v-model="selectedReservation.time" id="editTime" required>
              <option v-for="time in editAvailableTimes" :key="time" :value="time">
                {{ formatTimeTo12Hour(time) }}
              </option>
            </select>
            <p v-if="editTimeError">{{ editTimeError }}</p>
          </div>
          <div>
            <label for="editPeople">Número de personas:</label>
            <input type="number" v-model="selectedReservation.people" id="editPeople" required min="1">
          </div>
          <div>
            <label for="editTable">Número de Mesa:</label>
            <input type="text" v-model="selectedReservation.tableNumber" id="editTable" />
          </div>
          <button type="submit">Actualizar</button>
          <button type="button" @click="closeEditModal">Cancelar</button>
        </form>
      </div>
    </div>
<!-- Modal de Mensajes -->
<div v-if="showMessageModal" class="modal">
  <div class="modal-content">
    <h2>Conversación con {{ currentReservation.name }}</h2>

    <!-- Historial de mensajes -->
    <div class="message-history">
      <div v-for="message in messageHistory" :key="message.id" :class="message.sender === 'admin' ? 'admin-message' : 'client-message'">
        <p>{{ message.content }}</p>
        <span>{{ new Date(message.timestamp).toLocaleString() }}</span> <!-- Formatear la fecha correctamente -->
      </div>
    </div>

    <!-- Enviar nuevo mensaje -->
    <textarea v-model="newMessage" placeholder="Escribe tu mensaje aquí..."></textarea>
    <button @click="sendMessage">Enviar</button>

    <button @click="closeMessageModal">Cerrar</button>
  </div>
</div>


  </div>
</template>


<script>
import { ref, onMounted, watch } from 'vue';
import { collection, query, where, onSnapshot, doc, updateDoc, deleteDoc, addDoc, getDoc } from 'firebase/firestore'; // 'getDocs' ha sido eliminado

import { db } from '../firebase/init';
import QrScanner from 'qr-scanner'; // Agrega esta línea
import AdminReservationView from './AdminReservationView.vue';
import QRCodeView from './QRCodeView.vue';
import ReservationHistoryView from './ReservationHistoryView.vue';

export default {
  name: 'AdminPanel',
  components: {
    AdminReservationView,
    QRCodeView,
    ReservationHistoryView
     
  },
  setup() {
    const searchQuery = ref('');
    const reservations = ref([]);
    const filteredReservations = ref([]);
    const pendingReservations = ref([]);
    const newReservationAlert = ref(false);
    const selectedReservation = ref(null);
    const showEditModal = ref(false);
    const showReservationModal = ref(false);
    const showScannedReservationsModal = ref(false);
    const showMessageModal = ref(false);
    const currentReservation = ref(null);
    const messageHistory = ref([]);
    const newMessage = ref('');
    const loading = ref(false);
    const scanning = ref(false);
    const videoRef = ref(null);
    const showQRCodeModal = ref(false);
    const scannedReservationId = ref(null);
    const showScheduleModal = ref(false); // Estado del modal
    const specialNotes = ref('');


  
    const normalizePhone = (phone) => phone.replace(/[^0-9]/g, '');
    const disabledTimes = ref({}); // Horarios desactivados
    const selectedDate = ref(''); // Fecha seleccionada
    const OPENING_HOUR = 11;
    const CLOSING_HOUR = 21;
    const SLOT_INTERVAL_MINUTES = 30;
    const allowedDays = [3, 4, 5, 6, 0];

    const generateTimeSlots = () => {
      const slots = [];
      let minutes = OPENING_HOUR * 60;
      const closingMinutes = CLOSING_HOUR * 60;

      while (minutes <= closingMinutes) {
        const hour = Math.floor(minutes / 60);
        const minute = minutes % 60;
        const hourStr = String(hour).padStart(2, '0');
        const minuteStr = String(minute).padStart(2, '0');
        slots.push(`${hourStr}:${minuteStr}`);
        minutes += SLOT_INTERVAL_MINUTES;
      }

      return slots;
    };

    const allPossibleTimes = generateTimeSlots();
    const editAvailableTimes = ref([]);
    const editTimeError = ref('');


const formatTimeTo12Hour = (time) => {
  const [hour, minute] = time.split(':');
  const hour12 = hour % 12 || 12; // Convertir a formato de 12 horas
  const suffix = hour >= 12 ? 'PM' : 'AM';
  return `${hour12}:${minute} ${suffix}`;
};



// Iniciar escaneo de QR
    const startQRCodeScanner = () => {
      scanning.value = true;

      QrScanner.hasCamera().then((hasCamera) => {
        if (hasCamera) {
          const qrScanner = new QrScanner(
            videoRef.value,
            (result) => {
              scanning.value = false;
              handleScannedResult(result);
            }
          );
          qrScanner.start();
        } else {
          alert('No se encontró una cámara en este dispositivo.');
          scanning.value = false;
        }
      });
    };

    // Manejar el resultado escaneado y buscar reservación por `id`
    const handleScannedResult = async (result) => {
      try {
        console.log("ID escaneado:", result); // Verificamos que el ID sea correcto
        const reservationRef = doc(db, 'reservations', result); // Buscamos la reservación por `id`
        const docSnap = await getDoc(reservationRef);

        if (docSnap.exists()) {
          selectedReservation.value = docSnap.data(); // Asignamos los datos de la reservación escaneada
          console.log("Detalles de la reservación:", selectedReservation.value);

          // Actualizamos el estado de la reservación a 'used'
          await updateDoc(reservationRef, { status: 'used' });
          showQRCodeModal.value = true; // Mostramos el modal con los detalles de la reservación
        } else {
          alert('No se encontró la reservación.');
        }
      } catch (error) {
        console.error('Error al obtener los detalles de la reservación:', error);
        alert('Error al obtener los detalles de la reservación.');
      }
    };

    // Función para cerrar el modal de QR Code
    const closeQRCodeModal = () => {
      showQRCodeModal.value = false;
      selectedReservation.value = null; // Limpiar la reservación seleccionada al cerrar el modal
    };



  onMounted(() => {
  const today = new Date().toISOString().split('T')[0];

  // Consulta para obtener las reservaciones a partir de la fecha actual
  const reservationsQuery = query(
    collection(db, 'reservations'),
    where('date', '>=', today)
  );

  // Escucha cambios en las reservaciones
  onSnapshot(reservationsQuery, (snapshot) => {
    reservations.value = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    console.log('Reservaciones cargadas:', reservations.value);

    // Llamar las funciones que ya existían
    filterReservations();
    updatePendingReservations();
  });

  // Referencia para administrar configuraciones de Firestore
  const adminConfigRef = doc(db, 'adminConfig', 'reservations');

  // Escucha cambios en la configuración de administradores
  onSnapshot(adminConfigRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      
      // Detecta nuevas reservaciones
      if (data.newReservation) {
        newReservationAlert.value = true;
      }

      // Carga los horarios desactivados
      disabledTimes.value = data.disabledTimes || {};
      console.log('Horarios desactivados cargados:', disabledTimes.value);
    } else {
      console.log('No se encontró configuración de administrador.');
    }
  });
});

watch(
  () => selectedReservation.value?.date,
  () => {
    if (showEditModal.value) {
      refreshEditTimes();
    }
  }
);

watch(
  () => disabledTimes.value,
  () => {
    if (showEditModal.value) {
      refreshEditTimes();
    }
  },
  { deep: true }
);


const filterReservations = () => {
    console.log('Filtrando reservaciones...');
    const searchLower = searchQuery.value.toLowerCase();

    filteredReservations.value = reservations.value.filter(reservation => {
      const emailMatches = reservation.email?.toLowerCase().includes(searchLower);
      const phoneMatches = normalizePhone(reservation.phone || '').includes(searchQuery.value.replace(/[^0-9]/g, ''));
      const isNotUsed = reservation.status !== 'used';

      console.log(`Email match: ${emailMatches}, Phone match: ${phoneMatches}, Status: ${reservation.status}`);
      
      return (emailMatches || phoneMatches) && isNotUsed;
    }).sort((a, b) => {
      const dateA = new Date(`${a.date}T${a.time}`);
      const dateB = new Date(`${b.date}T${b.time}`);
      return dateA - dateB;
    });

    console.log('Reservaciones filtradas:', filteredReservations.value);
  };




    const formatTime = (time) => {
      const [hour, minute] = time.split(':');
      const date = new Date();
      date.setHours(hour, minute);
      const options = { hour: '2-digit', minute: '2-digit', hour12: true };
      return new Intl.DateTimeFormat('es-ES', options).format(date);
    };

    const updatePendingReservations = () => {
      pendingReservations.value = reservations.value.filter(reservation => reservation.status === 'pending');
    };

    const getStatusClass = (status) => {
      if (status === 'confirmed') return 'confirmed';
      if (status === 'denied') return 'denied';
      if (status === 'cancelled') return 'cancelled';
      return 'pending';
    };

// Función para confirmar la reservación y enviar el correo con QR
const confirmReservation = async (reservation) => {
  loading.value = true;

  try {
    // Datos del correo y QR
    const payload = {
      id: reservation.id,
      name: reservation.name,
      email: reservation.email,
      date: reservation.date,
      time: reservation.time,
      qrCodeUrl: `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${reservation.id}`,
    };

    // Llamada a la Cloud Function
    const response = await fetch('https://us-central1-merced-booking.cloudfunctions.net/sendReservationConfirmationWithQR', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    // Verificar respuesta del servidor
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al enviar el correo de confirmación.');
    }

    // Actualizar el estado de la reservación en Firestore
    const reservationRef = doc(db, 'reservations', reservation.id);
    await updateDoc(reservationRef, { status: 'confirmed' });

    // Confirmación local
    reservation.status = 'confirmed';
    alert('Reservación confirmada y correo enviado.');
  } catch (error) {
    console.error('Error al confirmar la reservación:', error);
    alert(`Error al confirmar la reservación: ${error.message}`);
  } finally {
    loading.value = false;
  }
};

// Función para denegar la reservación
const denyReservation = async (reservation) => {
  loading.value = true;

  try {
    // Actualizar estado a "denied" en Firestore
    const reservationRef = doc(db, 'reservations', reservation.id);
    await updateDoc(reservationRef, { status: 'denied' });

    // Enviar notificación de denegación
    const response = await fetch('https://us-central1-asador-1293f.cloudfunctions.net/sendReservationDenial', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: reservation.id, name: reservation.name, email: reservation.email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Error al enviar la notificación de denegación.');
    }

    // Confirmación local
    reservation.status = 'denied';
    alert('Reservación denegada y notificación enviada con éxito.');
  } catch (error) {
    console.error('Error al denegar la reservación:', error);
    alert(`Error al denegar la reservación: ${error.message}`);
  } finally {
    loading.value = false;
  }
};


    const cancelReservation = async (reservation) => {
      loading.value = true;
      try {
        const reason = prompt('Por favor, ingrese la razón de la cancelación:');
        if (!reason) {
          alert('Debe proporcionar una razón para la cancelación.');
          loading.value = false;
          return;
        }

        const response = await fetch('https://us-central1-merced-booking.cloudfunctions.net/cancelReservation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reservationId: reservation.id, reason })
        });

        if (!response.ok) {
          throw new Error('Error al cancelar la reservación');
        }

        alert('Reservación cancelada con éxito');
        reservation.status = 'cancelled';
      } catch (error) {
        console.error('Error al cancelar la reservación:', error);
        alert('Error al cancelar la reservación y enviar la notificación');
      } finally {
        loading.value = false;
      }
    };



    const deleteReservation = async (reservation) => {
      loading.value = true;
      try {
        const reservationRef = doc(db, 'reservations', reservation.id);
        await deleteDoc(reservationRef);
        alert('Reservación eliminada con éxito');
      } catch (error) {
        console.error('Error al eliminar la reservación:', error);
      } finally {
        loading.value = false;
      }
    };

    const selectReservationForAction = (reservationId) => {
      const element = document.getElementById(`reservation-${reservationId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const refreshEditTimes = () => {
      editTimeError.value = '';
      editAvailableTimes.value = [];

      if (!selectedReservation.value || !selectedReservation.value.date) {
        editTimeError.value = 'Selecciona una fecha válida.';
        return;
      }

      const dayIndex = new Date(selectedReservation.value.date).getDay();
      if (!allowedDays.includes(dayIndex)) {
        editTimeError.value = 'Las reservaciones solo están disponibles de miércoles a domingo.';
        selectedReservation.value.time = '';
        return;
      }

      const disabled = disabledTimes.value[selectedReservation.value.date] || [];
      const available = allPossibleTimes.filter((time) => !disabled.includes(time));

      // Mantener la hora actual si ya estaba seleccionada aunque esté desactivada, para no perderla
      if (
        selectedReservation.value.time &&
        !available.includes(selectedReservation.value.time)
      ) {
        available.push(selectedReservation.value.time);
        available.sort();
      }

      if (!available.length) {
        editTimeError.value = 'No hay horarios disponibles para esta fecha, habilita alguno antes de editar.';
      }

      editAvailableTimes.value = available;
    };

    const openEditModal = (reservation) => {
      selectedReservation.value = {
        ...reservation,
        tableNumber: reservation.tableNumber || ''
      };
      showEditModal.value = true;
      refreshEditTimes();
    };

    const closeEditModal = () => {
      showEditModal.value = false;
      editTimeError.value = '';
      editAvailableTimes.value = [];
    };

    const openReservationModal = () => {
      showReservationModal.value = true;
    };

    const closeReservationModal = () => {
      showReservationModal.value = false;
    };

    const openScannedReservationsModal = () => {
      showScannedReservationsModal.value = true;
    };

    const closeScannedReservationsModal = () => {
      showScannedReservationsModal.value = false;
    };

    const assignTable = async (reservation) => {
      loading.value = true;
      try {
        if (!reservation.tableNumberInput) {
          alert('Por favor ingrese un número de mesa.');
          loading.value = false;
          return;
        }

        const reservationRef = doc(db, 'reservations', reservation.id);
        await updateDoc(reservationRef, { tableNumber: reservation.tableNumberInput });

        reservation.tableNumber = reservation.tableNumberInput;
        reservation.tableAssigned = true;

        alert('Mesa asignada con éxito');
      } catch (error) {
        console.error('Error al asignar la mesa:', error);
        alert('Error al asignar la mesa');
      } finally {
        loading.value = false;
      }
    };
   

const openMessageModal = (reservation) => {
  if (!reservation) {
    console.error("No se proporcionó ninguna reservación.");
    return;
  }
  currentReservation.value = reservation;
  showMessageModal.value = true;
  loadMessageHistory(reservation);
};

// Función para cerrar el modal de mensajes
const closeMessageModal = () => {
  showMessageModal.value = false;
  currentReservation.value = null;
  newMessage.value = ''; // Limpia el campo de texto
};

/// Función para cargar historial de mensajes desde Firestore
const loadMessageHistory = (reservation) => {
  try {
    if (!reservation || !reservation.id) {
      console.error('Reserva no válida');
      return;
    }

    const messagesRef = collection(db, 'reservations', reservation.id, 'messages');

    // Escuchar en tiempo real los mensajes
    onSnapshot(messagesRef, (snapshot) => {
      messageHistory.value = snapshot.docs.map(doc => {
        const data = doc.data();

        // Asegúrate de que el timestamp se convierte correctamente en una fecha
        const timestamp = data.timestamp && typeof data.timestamp.toDate === 'function' 
          ? data.timestamp.toDate() 
          : new Date();

        return {
          ...data,
          timestamp, // Asegura que el campo timestamp es un objeto de fecha
          id: doc.id
        };
      });

      console.log('Historial de mensajes actualizado:', messageHistory.value);
    });
  } catch (error) {
    console.error('Error al cargar el historial de mensajes:', error);
  }
};

const sendMessage = async () => {
  if (!newMessage.value.trim()) {
    alert('No puedes enviar un mensaje vacío');
    return;
  }

  const message = {
    content: newMessage.value,
    timestamp: Date.now(),
    sender: 'admin',  // Identifica que el mensaje lo envía el administrador
  };

  try {
    // Guarda el mensaje en la base de datos
    await saveMessageToDB(currentReservation.value, message);

    // Actualiza el historial localmente
    messageHistory.value.push(message);

    alert('Mensaje guardado correctamente.');
  } catch (error) {
    console.error('Error al guardar el mensaje:', error);
    alert('Error al guardar el mensaje.');
  } finally {
    newMessage.value = ''; // Limpia el campo de texto
  }
};

// Función para guardar el mensaje en la base de datos
const saveMessageToDB = async (reservation, message) => {
  try {
    const messagesRef = collection(db, 'reservations', reservation.id, 'messages');
    await addDoc(messagesRef, {
      content: message.content,
      sender: message.sender,
      timestamp: message.timestamp,
      reservationId: reservation.id
    });
  } catch (error) {
    console.error('Error al guardar el mensaje:', error);
  }
};

  const updateReservation = async (reservation) => {
      loading.value = true;
      try {
        const reservationRef = doc(db, 'reservations', reservation.id);
        const docSnap = await getDoc(reservationRef);
        if (docSnap.exists()) {
          await updateDoc(reservationRef, {
            name: reservation.name,
            email: reservation.email,
            phone: reservation.phone,
            date: reservation.date,
            time: reservation.time,
            people: reservation.people,
            tableNumber: reservation.tableNumber,
            specialNotes: reservation.specialNotes
          });
          alert('Reservación actualizada con éxito');
          closeEditModal();
        } else {
          alert('Error: No se encontró la reservación.');
        }
      } catch (error) {
        console.error('Error al actualizar la reservación:', error);
      } finally {
        loading.value = false;
      }
    };

    // Verificar si un horario está desactivado
    const isTimeDisabled = (date, time) => {
      return disabledTimes.value[date]?.includes(time);
    };

    // Alternar estado de un horario
    const toggleTime = async (date, time) => {
      if (!disabledTimes.value[date]) {
        disabledTimes.value[date] = [];
      }

      if (isTimeDisabled(date, time)) {
        disabledTimes.value[date] = disabledTimes.value[date].filter((t) => t !== time);
      } else {
        disabledTimes.value[date].push(time);
      }

      // Guardar cambios en Firestore
      const adminConfigRef = doc(db, 'adminConfig', 'reservations');
      await updateDoc(adminConfigRef, { disabledTimes: disabledTimes.value });
      console.log(`Horario ${time} para ${date} actualizado.`);
    };

    // Cargar horarios desactivados para una fecha específica
    const loadTimesForDate = () => {
      if (selectedDate.value && !disabledTimes.value[selectedDate.value]) {
        disabledTimes.value[selectedDate.value] = [];
      }
    };

    const openScheduleModal = () => {
  showScheduleModal.value = true;
};

const closeScheduleModal = () => {
  showScheduleModal.value = false;
};



return {
  searchQuery,
  reservations,
  filteredReservations,
  pendingReservations,
  newReservationAlert,
  selectedReservation,
  showEditModal,
  showReservationModal,
  showScannedReservationsModal,
  loading,
  scanning,
  videoRef,
  showQRCodeModal,
  scannedReservationId,
  disabledTimes,
  selectedDate,
  allPossibleTimes,
  editAvailableTimes,
  editTimeError,
  showScheduleModal,
  openScheduleModal,
  closeScheduleModal,
  formatTimeTo12Hour, // Asegúrate de incluirlo aquí
  isTimeDisabled,
  toggleTime,
  loadTimesForDate,
  startQRCodeScanner,
  filterReservations,
  formatTime,
  updatePendingReservations,
  getStatusClass,
  confirmReservation,
  denyReservation,        // Envío por email
  cancelReservation,      // Envío por email
  deleteReservation,
  selectReservationForAction,
  openEditModal,
  closeEditModal,
  refreshEditTimes,
  openReservationModal,
  closeReservationModal,
  assignTable,
  openScannedReservationsModal,
  closeScannedReservationsModal,
  updateReservation,
  closeQRCodeModal,
  showMessageModal,
  currentReservation,
  messageHistory,
  newMessage,
  openMessageModal,
  closeMessageModal,
  sendMessage, 
  specialNotes,           // Solo guarda el mensaje en Firestore
};

  }
};
</script>
<style src="./AdminPanel.css"></style>
